<template>
  <Transition name="suggestion-slide">
    <div
      v-if="suggestion"
      data-slot="field-suggestion"
      :class="
        cn(
          'flex items-center gap-2 rounded-b-md border border-t-0 border-primary/20 bg-primary/5 px-3 py-1.5 text-sm text-foreground',
          invalid && 'border-destructive',
        )
      "
      role="status"
    >
      <LightbulbIcon class="size-4 shrink-0" aria-hidden="true" />
      <span class="min-w-0">
        {{ t(`pages.wizard.steps.settings.suggestions.${suggestion.message}`) }}
        <Button
          v-if="suggestion.suggested"
          type="button"
          variant="outline"
          size="xs"
          class="ml-1"
          @click="emit('accept', suggestion.suggested)"
        >
          <template v-if="resolvedLinkText">{{ resolvedLinkText }}</template>
          <code v-else class="text-xs">{{ suggestion.suggested }}</code>
        </Button>
      </span>
    </div>
  </Transition>
</template>

<script setup lang="ts">
// A field's "did you mean" hint, joined to the bottom edge of the control it is
// about: no top border, the control's own bottom corners squared off by
// FieldRow, and the field's error border when the value is also invalid, so the
// box, the hint and its fix read as one piece. FieldRow renders it from a
// field's `suggest`; nothing else places it.
import { LightbulbIcon } from "lucide-vue-next";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { Button } from "@/components/ui/button";
import type { FieldSuggestion } from "@/composables/useUrlFieldSuggestion";
import { cn } from "@/lib/utils";

const props = withDefaults(
  defineProps<{
    suggestion: FieldSuggestion | null;
    // Words the fix button instead of printing the suggested value.
    linkText?: string;
    invalid?: boolean;
  }>(),
  { linkText: undefined, invalid: false },
);

const emit = defineEmits<{ accept: [suggested: string] }>();

const { t } = useI18n();

const resolvedLinkText = computed(() => {
  if (props.linkText) return props.linkText;
  if (props.suggestion?.linkMessage)
    return t(`pages.wizard.steps.settings.suggestions.${props.suggestion.linkMessage}`);
  return null;
});
</script>

<style scoped>
.suggestion-slide-enter-active,
.suggestion-slide-leave-active {
  transition: all 0.2s ease;
}

.suggestion-slide-enter-from,
.suggestion-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
